import { useCallback, useState } from "react";
import type { StampDto } from "@sikilesi/protocol";
import { useServices } from "../components/Services";
import { ApiError, errorText } from "../lib/api";
import { isStamped, markStamped } from "../lib/stamped-store";

export type StampPhase = "idle" | "verifying" | "sending" | "done" | "error";

export interface StampFlow {
  phase: StampPhase;
  stamped: boolean;
  result: StampDto | null;
  error: string | null;
  /** Open the Turnstile check. */
  begin: () => void;
  cancel: () => void;
  /** Called with the Turnstile token: sends the stamp. */
  submit: (token: string) => Promise<void>;
}

/** SİKİLESİ stamp: Turnstile → POST /topics/:id/stamp → new count & level. */
export function useStamp(topicId: string, onStamped?: (s: StampDto) => void): StampFlow {
  const { api } = useServices();
  const [phase, setPhase] = useState<StampPhase>("idle");
  const [stamped, setStamped] = useState(() => isStamped(topicId));
  const [result, setResult] = useState<StampDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const begin = useCallback(() => {
    setError(null);
    setPhase("verifying");
  }, []);
  const cancel = useCallback(() => {
    setPhase("idle");
  }, []);

  const submit = useCallback(
    async (token: string) => {
      setPhase("sending");
      try {
        const stamp = await api.stamp(topicId, token);
        markStamped(topicId);
        setStamped(true);
        setResult(stamp);
        setPhase("done");
        onStamped?.(stamp);
      } catch (err) {
        if (err instanceof ApiError && err.code === "ALREADY_STAMPED") {
          markStamped(topicId);
          setStamped(true);
          setPhase("done");
          setError(errorText(err));
          return;
        }
        setError(errorText(err));
        setPhase("error");
      }
    },
    [api, topicId, onStamped],
  );

  return { phase, stamped, result, error, begin, cancel, submit };
}
